import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Briefcase, FileText, Copy, ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import { COMPANY } from "../lib/constants";
import { toast } from "sonner";
import Seo from "../components/Seo";
import useSiteSettings, { profilePdfUrl } from "../lib/useSiteSettings";
import CardHero from "../components/card/CardHero";
import ConnectTiles from "../components/card/ConnectTiles";
import CapabilityStrip from "../components/card/CapabilityStrip";
import { QrAside, IdentifiersBlock, QuickLinksBlock } from "../components/card/QrAside";

// ----- Helpers (pure) ----- //
function buildVCard(c) {
  const lines = [
    "BEGIN:VCARD",
    "VERSION:3.0",
    `N:${c.person_name || ""}`,
    `FN:${c.person_name || ""}`,
    `ORG:${COMPANY.name}`,
    `TITLE:${c.designation || ""}`,
    `TEL;TYPE=CELL,VOICE:+91${(c.phone || "").replace(/\D/g, "")}`,
    `TEL;TYPE=WORK,VOICE:+91${(c.whatsapp || c.phone || "").replace(/\D/g, "")}`,
    `EMAIL;TYPE=WORK:${c.email || ""}`,
    `URL:${c.website || ""}`,
    `ADR;TYPE=WORK:;;${c.address || ""};;;;`,
    "END:VCARD",
  ];
  return lines.join("\n");
}

function initialsOf(name = "") {
  const parts = name.trim().split(/\s+/).slice(0, 3);
  return parts.map((p) => p[0]).join("").toUpperCase() || "MI";
}

function formatPhone(p) {
  const d = (p || "").replace(/\D/g, "");
  return d.replace(/(\d{5})(\d{5})/, "$1 $2");
}

function whatsappLink(whatsapp, phone) {
  const wa = (whatsapp || phone || "").replace(/\D/g, "");
  const num = wa.startsWith("91") ? wa : `91${wa}`;
  return `https://wa.me/${num}?text=${encodeURIComponent("Hello Melopa Invents, I have a requirement.")}`;
}

const FALLBACK_CARD = {
  person_name: COMPANY.director,
  designation: COMPANY.designation,
  phone: "7975127454",
  whatsapp: "7975127454",
  email: COMPANY.email,
  website: COMPANY.website,
  address: COMPANY.address,
  map_link: "https://maps.google.com/?q=Bengaluru,Karnataka,India",
};

export default function Card() {
  const [bc, setBc] = useState(null);
  const settings = useSiteSettings();
  const pdf = profilePdfUrl(settings);
  const cardUrl = useMemo(
    () => (typeof window !== "undefined" ? `${window.location.origin}/card` : "https://melopainvents.com/card"),
    [],
  );

  useEffect(() => {
    let mounted = true;
    api
      .get("/business-card")
      .then((r) => {
        if (mounted) setBc(r.data);
      })
      .catch((err) => {
        // Non-fatal: fall back to constants while displaying the page.
        console.warn("Failed to load business card:", err?.message || err);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const c = bc || FALLBACK_CARD;
  const phoneDigits = (c.phone || "").replace(/\D/g, "");
  const phoneFmt = formatPhone(c.phone);
  const waLink = whatsappLink(c.whatsapp, c.phone);
  const websiteHost = (c.website || "").replace(/^https?:\/\//, "");
  const initials = initialsOf(c.person_name);

  const downloadVcf = useCallback(() => {
    const data = buildVCard(c);
    const blob = new Blob([data], { type: "text/vcard;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(c.person_name || "melopa-invents").replace(/\s+/g, "_")}.vcf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success("Contact saved (.vcf)");
  }, [c]);

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(cardUrl);
      toast.success("Link copied");
    } catch {
      toast.error("Copy failed");
    }
  }, [cardUrl]);

  const shareCard = useCallback(async () => {
    const shareData = {
      title: `${COMPANY.name} — Digital Card`,
      text: `${c.person_name}, ${c.designation} — ${COMPANY.name}`,
      url: cardUrl,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
        return;
      }
    } catch {
      /* fall through to clipboard */
    }
    await copyLink();
  }, [c, cardUrl, copyLink]);

  return (
    <div className="py-6 md:py-12 bg-soft min-h-[calc(100vh-64px)]" data-testid="digital-card-page">
      <Seo title="Digital Business Card" description={`${c.person_name}, ${c.designation} — ${COMPANY.name}`} path="/card" />
      <div className="container-x grid lg:grid-cols-12 gap-6 lg:gap-8">
        <div className="lg:col-span-7">
          <div className="card-premium overflow-hidden shadow-elevated relative">
            <CardHero
              initials={initials}
              name={c.person_name}
              designation={c.designation}
              phoneDigits={phoneDigits}
              waLink={waLink}
              onShare={shareCard}
              onSave={downloadVcf}
            />
            <ConnectTiles
              phoneFmt={phoneFmt}
              phoneDigits={phoneDigits}
              waLink={waLink}
              email={c.email}
              website={c.website}
              websiteHost={websiteHost}
              address={c.address}
              mapLink={c.map_link}
            />
            <CapabilityStrip />
            <div className="p-5 md:p-7 mt-2 border-t border-soft flex flex-wrap gap-2">
              {pdf ? (
                <a href={pdf} target="_blank" rel="noreferrer" className="btn-primary" data-testid="card-profile-download">
                  <FileText className="w-4 h-4" /> Download Company Profile
                </a>
              ) : (
                <Link to="/downloads" className="btn-outline" data-testid="card-profile-download">
                  <FileText className="w-4 h-4" /> Download Company Profile
                </Link>
              )}
              <Link to="/submit" className="btn-gold" data-testid="card-submit">
                Submit Requirement <ArrowRight className="w-4 h-4" />
              </Link>
              <Link to="/products" className="btn-outline">
                <Briefcase className="w-4 h-4" /> Products & Services
              </Link>
              <button onClick={copyLink} className="btn-outline" data-testid="card-copy">
                <Copy className="w-4 h-4" /> Copy Link
              </button>
            </div>
          </div>
        </div>

        <aside className="lg:col-span-5 space-y-4">
          <QrAside cardUrl={cardUrl} onShare={shareCard} onCopy={copyLink} />
          <IdentifiersBlock />
          <QuickLinksBlock />
        </aside>
      </div>
    </div>
  );
}
